import _ from 'lodash';
import { createSelector } from 'reselect';

import { getSystem } from './system.selectors';

const selectAiProcessesState = createSelector(getSystem, ({ aiProcesses }) => aiProcesses);

export const selectAiProcesses = createSelector(
  selectAiProcessesState,
  ({ aiProcesses }) => aiProcesses,
);

export const selectGlobalAiProcesses = createSelector(
  selectAiProcessesState,
  ({ globalAiProcesses }) => globalAiProcesses,
);

export const selectAiProcessById = id =>
  createSelector(selectAiProcesses, aiProcesses =>
    aiProcesses.find(aiProcess => aiProcess.id === id),
  );

export const selectAiProcessFormSchema = id =>
  createSelector(selectAiProcessById(id), aiProcess => aiProcess?.formSchema ?? null);

export const selectBusyAiProcessesNames = _.memoize(id =>
  createSelector(selectAiProcesses, aiProcesses =>
    aiProcesses.reduce((acc, aiProcess) => {
      if (aiProcess.id !== id) {
        acc.push(aiProcess.name);
      }

      return acc;
    }, []),
  ),
);

export const selectBusyAiProcessStepsNames = _.memoize(
  (aiProcessId, stepId) =>
    createSelector(
      selectAiProcessById(aiProcessId),
      aiProcess =>
        aiProcess?.steps?.reduce((acc, step) => {
          if (step.id !== stepId) {
            acc.push(step.name);
          }

          return acc;
        }, []) ?? [],
    ),
  (aiProcessId, stepId) => `${aiProcessId}_${stepId}`,
);

export const selectAvailableStepOrderNumber = aiProcessId =>
  createSelector(selectAiProcessById(aiProcessId), aiProcess => {
    return (_.last(_.orderBy(aiProcess?.steps, 'orderNumber', 'asc'))?.orderNumber ?? 0) + 1;
  });

export const selectAiProcessStepToolsOptions = createSelector(selectAiProcessesState, ({ tools }) =>
  tools.map(({ name }) => ({ name, value: name })),
);

export const selectAiProcessStepWriterToolNames = createSelector(
  selectAiProcessesState,
  ({ tools }) => new Set(tools.filter(({ isWriter }) => isWriter).map(({ name }) => name)),
);

export const selectAiProcessStepResourcesOptions = createSelector(
  selectAiProcessesState,
  ({ resources }) => resources.map(({ name, uriPattern }) => ({ name, value: uriPattern })),
);

export const selectIsPendingAiProcessStepsReordering = createSelector(
  selectAiProcessesState,
  ({ isPendingReordering }) => isPendingReordering,
);

export const selectPreviousStepsOptions = _.memoize(
  (aiProcessId, currentStepOrderNumber) =>
    createSelector(selectAiProcessById(aiProcessId), aiProcess =>
      (aiProcess?.steps ?? []).reduce((acc, step) => {
        if (step.orderNumber < currentStepOrderNumber) {
          acc.push({ value: step.id, label: step.name });
        }

        return acc;
      }, []),
    ),
  (aiProcessId, currentStepOrderNumber) => `${aiProcessId}_${currentStepOrderNumber}`,
);
